import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import { StoreState } from '../../reducers';
import { Label, Button, ButtonsList, ResourceType } from './styles';
import { Api } from '../../typings/api';
import FromApp from '../../containers/ResourceSelector/DrivePicker/FromApp';
import FromSelection from '../../containers/ResourceSelector/DrivePicker/FromSelection';
import ImportManager from '../../containers/ImportManager';
import ProgressBar from './ProgressBar';
import { FileType, FileError, removeFileFromImport } from '../../containers/ImportManager/actions';
import Utils from '../../utils';

export enum ResourceSelectorTheme {
	DARK = 'DARK',
	LIGHT = 'LIGHT',
}

interface ResourceSelectorProps {
	id: string;
	label?: string;
	isValidResourceFromApp: (item: Api.DriveItem) => boolean;
	fileExtensions?: string[];
	isFileExtensionsDisplayed?: boolean;
	isOptimizedImageImport?: boolean;
	isPublicationDependency?: boolean;
	fileType?: FileType;
	isMultipleSelection?: boolean;
	linkPanel?: React.ReactNode;
	isImportingFile: boolean;
	importingPercentage: number;
	importingError: FileError | null;
	isProcessingVideo: boolean;
	onSelect: (items: Api.DriveItem[]) => void;
	customImportHandler?: (file: File | File[], isCorrectFileTypes: boolean) => void;
	onFileImportCancel?: () => void;
	actionButtonColor?: string;
	isFromSelection?: boolean;
	className?: string;
	theme?: ResourceSelectorTheme;
	dispatch: Dispatch<StoreState>;
}

enum ButtonTypes {
	desktop = 'from_desktop',
	app = 'from_app',
	youtube = 'from_youtube',
	selection = 'from_selection',
	drive = 'from_drive',
}

interface IconConfig {
	name: string;
	width: string;
	height: string;
}

interface ButtonsConfig {
	[k: string]: {
		label: ButtonTypes;
		icon?: IconConfig;
		picker?: React.ReactNode;
		component?: React.ReactNode;
		onClick?: () => void;
	};
}

const ResourceSelector = ({
	id,
	label,
	isValidResourceFromApp,
	fileExtensions = [],
	isFileExtensionsDisplayed,
	isOptimizedImageImport,
	isPublicationDependency,
	fileType,
	isMultipleSelection,
	linkPanel,
	isImportingFile,
	importingPercentage,
	importingError,
	isProcessingVideo,
	onSelect,
	customImportHandler,
	onFileImportCancel,
	actionButtonColor,
	isFromSelection,
	className,
	theme = ResourceSelectorTheme.LIGHT,
	dispatch,
}: ResourceSelectorProps) => {
	const [activePicker, setActivePicker] = React.useState('');

	const handleSelect = (items: Api.DriveItem[]) => {
		if (importingError) {
			handleFileImportCancel();
		}
		onSelect(items);
	};

	const handleFileImportCancel = () => {
		if (onFileImportCancel) {
			onFileImportCancel();
		} else {
			dispatch(removeFileFromImport(id));
		}
	};

	const buttonsConfig: ButtonsConfig = {
		[ButtonTypes.desktop]: {
			label: ButtonTypes.desktop,
			component: (
				<ImportManager
					id={id}
					triggerComponent={
						<Button
							as="div"
							iconWidth="21px"
							iconHeight="20px"
						>
							<Translate value="messages.from_desktop" />
							{Utils.getSvgIcon('iconset_uprez_from_desktop')}
						</Button>
					}
					extensions={fileExtensions.join(', ')}
					onImport={(item) => customImportHandler ? undefined : handleSelect([item])}
					customImportHandler={customImportHandler}
					fileType={fileType}
					isOptimizedImageImport={isOptimizedImageImport}
					isPublicationDependency={isPublicationDependency}
					isMultiple={isMultipleSelection}
				/>
			),
		},
		[ButtonTypes.app]: {
			label: Utils.isEmbedMode() ? ButtonTypes.drive : ButtonTypes.app,
			icon: {
				name: 'iconset_uprez_from_drive',
				width: '24px',
				height: '24px',
			},
			picker: (
				<FromApp
					isValidResource={isValidResourceFromApp}
					onSelect={handleSelect}
					onClose={() => setActivePicker('')}
					actionButtonColor={actionButtonColor}
					isMultipleSelection={isMultipleSelection}
					className={className}
				/>
			),
			onClick: () => setActivePicker(ButtonTypes.app),
		},
	};

	if (linkPanel) {
		buttonsConfig[ButtonTypes.youtube] = {
			label: ButtonTypes.youtube,
			icon: {
				name: 'iconset_youtube_logo',
				width: '22px',
				height: '22px',
			},
			picker: linkPanel,
			onClick: () => setActivePicker(ButtonTypes.youtube),
		};
	}

	if (isFromSelection) {
		buttonsConfig[ButtonTypes.selection] = {
			label: ButtonTypes.selection,
			icon: {
				name: 'iconset_favorite',
				width: '22px',
				height: '22px',
			},
			picker: (
				<FromSelection
					isValidResource={isValidResourceFromApp}
					onSelect={handleSelect}
					onClose={() => setActivePicker('')}
					actionButtonColor={actionButtonColor}
					className={className}
				/>
			),
			onClick: () => setActivePicker(ButtonTypes.selection),
		};
	}

	if (isImportingFile && !importingError) {
		return (
			<ProgressBar
				percentage={importingPercentage}
				error={importingError}
				isProcessing={isProcessingVideo}
				processingLabel="messages.video_processing"
				theme={theme}
				hasCancelButton={true}
				onCancel={handleFileImportCancel}
			/>
		);
	}

	return (
		<ThemeProvider theme={{ theme }}>
			{activePicker
				? <>{buttonsConfig[activePicker].picker}</>
				: (
					<>
						{isImportingFile && importingError &&
							<ProgressBar
								percentage={importingPercentage}
								error={importingError}
								isProcessing={isProcessingVideo}
								processingLabel="messages.video_processing"
								theme={theme}
								hasCancelButton={true}
								onCancel={handleFileImportCancel}
							/>
						}
						{label && <Label value={label} />}
						<ButtonsList>
							{Object.values(buttonsConfig).map(({ label, icon, component, onClick }) => (
								<li key={label}>
									{component || (
										<Button
											type="button"
											onClick={onClick}
											iconWidth={icon ? icon.width : ''}
											iconHeight={icon ? icon.height : ''}
										>
											<Translate value={`messages.${label}`} />
											{icon && Utils.getSvgIcon(icon.name)}
										</Button>
									)}
								</li>
							))}
						</ButtonsList>
						{isFileExtensionsDisplayed &&
							<ResourceType>
								<Translate value="messages.type" />
								{`: ${fileExtensions.map(item => item.substring(1)).join(', ')}`}
							</ResourceType>
						}
					</>
				)
			}
		</ThemeProvider>
	);
};

export default ResourceSelector;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ResourceSelector/index.tsx